import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createTemplate,
  getTemplate,
  updateTemplate,
  LANGUAGE_REGEX,
  TEMPLATE_NAME_REGEX,
  type OutboundTemplate,
  type OutboundTemplateCreate,
  type OutboundTemplatePatch,
  type TemplateCategory,
  type TemplateStatus,
  type TemplateType,
} from '../api/templates.js';
import { listConnectors } from '../api/connectors.js';
import { ApiError } from '../api/apiClient.js';
import { TagInput } from '../components/TagInput.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';
import { TemplateComponentsBuilder, useTemplateComponentsBuilder, builderToComponents } from './TemplateComponentsBuilder.js';
import { createProviderTemplate } from '../api/providerTemplates.js';

// Lazy-load Monaco — mismo patrón que FlowEditor.
const MonacoEditor = lazy(async () => {
  const mod = await import('@monaco-editor/react');
  return { default: mod.default };
});

const CATEGORIES: TemplateCategory[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const TEMPLATE_TYPES: TemplateType[] = [
  'standard',
  'carousel',
  'limited_time_offer',
  'media',
];
const STATUSES: TemplateStatus[] = ['draft', 'pending', 'approved', 'rejected'];
const LANG_PRESETS = ['es', 'es-CL', 'en', 'en-US', 'pt-BR'];

interface FormValues {
  name: string;
  templateName: string;
  connectorId: string;
  language: string;
  category: TemplateCategory;
  templateType: TemplateType;
  status: TemplateStatus;
  bodyText: string;
  placeholders: string[];
  componentsJson: string;
  providerTemplateId: string;
  externalId: string;
  namespace: string;
}

const emptyValues: FormValues = {
  name: '',
  templateName: '',
  connectorId: '',
  language: 'es-CL',
  category: 'UTILITY',
  templateType: 'standard',
  status: 'draft',
  bodyText: '',
  placeholders: [],
  componentsJson: '',
  providerTemplateId: '',
  externalId: '',
  namespace: '',
};

export const templateFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  templateName: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(TEMPLATE_NAME_REGEX, { message: 'TEMPLATE_NAME_INVALID' }),
  connectorId: z.string().min(1, { message: 'CONNECTOR_REQUIRED' }),
  language: z.string().regex(LANGUAGE_REGEX, { message: 'LANGUAGE_INVALID' }),
  category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
  templateType: z.enum(['standard', 'carousel', 'limited_time_offer', 'media']),
  status: z.enum(['approved', 'pending', 'rejected', 'draft']),
  bodyText: z.string().trim().min(1).max(1024),
  placeholders: z.array(z.string()).max(20, { message: 'PLACEHOLDERS_MAX' }),
});

/**
 * Parsea componentsJson (string) → undefined | array. Lanza si JSON inválido o
 * si no es array.
 */
function parseComponents(
  raw: string,
): Array<Record<string, unknown>> | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error('COMPONENTS_INVALID_JSON');
  }
  if (!Array.isArray(parsed)) throw new Error('COMPONENTS_MUST_BE_ARRAY');
  return parsed as Array<Record<string, unknown>>;
}

function valuesToCreate(
  v: FormValues,
  components: Array<Record<string, unknown>> | undefined,
): OutboundTemplateCreate {
  const out: OutboundTemplateCreate = {
    connectorId: v.connectorId,
    name: v.name.trim(),
    templateName: v.templateName.trim(),
    language: v.language,
    category: v.category,
    templateType: v.templateType,
    status: v.status,
    bodyText: v.bodyText,
    placeholders: v.placeholders,
  };
  if (components) out.components = components;
  if (v.providerTemplateId) out.providerTemplateId = v.providerTemplateId;
  if (v.externalId) out.externalId = v.externalId;
  if (v.namespace) out.namespace = v.namespace;
  return out;
}

function buildPatch(
  current: OutboundTemplate,
  v: FormValues,
  components: Array<Record<string, unknown>> | undefined,
): OutboundTemplatePatch {
  const patch: OutboundTemplatePatch = {};
  const setIfDiff = <K extends keyof OutboundTemplatePatch>(
    key: K,
    next: OutboundTemplatePatch[K],
    curr: OutboundTemplatePatch[K] | undefined,
  ): void => {
    if (JSON.stringify(next ?? null) !== JSON.stringify(curr ?? null)) {
      patch[key] = next;
    }
  };
  setIfDiff('name', v.name.trim(), current.name);
  setIfDiff('templateName', v.templateName.trim(), current.templateName);
  setIfDiff('language', v.language, current.language);
  setIfDiff('category', v.category, current.category);
  setIfDiff('templateType', v.templateType, current.templateType);
  setIfDiff('status', v.status, current.status);
  setIfDiff('bodyText', v.bodyText, current.bodyText);
  setIfDiff('placeholders', v.placeholders, current.placeholders);
  setIfDiff('components', components, current.components);
  setIfDiff(
    'providerTemplateId',
    v.providerTemplateId || undefined,
    current.providerTemplateId,
  );
  setIfDiff('externalId', v.externalId || undefined, current.externalId);
  setIfDiff('namespace', v.namespace || undefined, current.namespace);
  return patch;
}

export interface TemplateFormProps {
  mode: 'create' | 'edit';
  /** Para tests sin DOM: deshabilita Monaco. */
  disableEditor?: boolean;
}

export function TemplateForm({
  mode,
  disableEditor = false,
}: TemplateFormProps): JSX.Element {
  const { id = '', templateId = '' } = useParams<{
    id: string;
    templateId?: string;
  }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id, { includeArchived: false }],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });

  const templateQuery = useQuery({
    queryKey: ['template', id, templateId],
    queryFn: () => getTemplate(id, templateId),
    enabled: mode === 'edit' && Boolean(id && templateId),
  });

  const defaultValues = useMemo<FormValues>(() => {
    if (mode === 'edit' && templateQuery.data) {
      const tpl = templateQuery.data;
      return {
        name: tpl.name,
        templateName: tpl.templateName,
        connectorId: tpl.connectorId,
        language: tpl.language,
        category: tpl.category,
        templateType: tpl.templateType,
        status: tpl.status,
        bodyText: tpl.bodyText,
        placeholders: tpl.placeholders ?? [],
        componentsJson: tpl.components
          ? JSON.stringify(tpl.components, null, 2)
          : '',
        providerTemplateId: tpl.providerTemplateId ?? '',
        externalId: tpl.externalId ?? '',
        namespace: tpl.namespace ?? '',
      };
    }
    return emptyValues;
  }, [mode, templateQuery.data]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const bodyText = watch('bodyText');
  const componentsJson = watch('componentsJson');
  const watchedConnectorId = watch('connectorId');
  const watchedTemplateName = watch('templateName');
  const watchedLanguage = watch('language');
  const watchedCategory = watch('category');

  // #162 — Builder visual (toggleable). Sincroniza con componentsJson.
  const [componentsMode, setComponentsMode] = useState<'visual' | 'json'>('visual');
  const { builder, setBuilder } = useTemplateComponentsBuilder(
    componentsJson,
    (next) => setValue('componentsJson', next),
  );

  const createMutation = useMutation({
    mutationFn: (body: OutboundTemplateCreate) => createTemplate(id, body),
    onSuccess: () => {
      pushToast('success', t.template.created);
      queryClient.invalidateQueries({ queryKey: ['templates', id] });
      navigate(`/tenants/${id}/templates`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        if (e.status === 422 && e.code === 'INVALID_CONNECTOR') {
          pushToast('error', t.template.invalidConnector);
          return;
        }
        pushToast('error', e.message);
        return;
      }
      pushToast('error', (e as Error).message);
    },
  });

  // #162 — Submit a provider (Meta Graph / 360dialog).
  const submitToProviderMut = useMutation({
    mutationFn: async () => {
      if (!watchedConnectorId) throw new Error('Elegí un connector primero');
      const components = builderToComponents(builder);
      if (components.length === 0) throw new Error('Definí al menos un componente (header/body/...)');
      const hasBody = components.some((c) => c['type'] === 'BODY');
      if (!hasBody) throw new Error('Body requerido');
      const payload = {
        name: watchedTemplateName,
        language: watchedLanguage.replace('-', '_'), // Meta usa es_CL en lugar de es-CL
        category: watchedCategory,
        components,
      };
      return createProviderTemplate(id, watchedConnectorId, payload);
    },
    onSuccess: (res) => {
      pushToast('success', `Template enviado al provider${res.id ? ` (id ${res.id})` : ''}`);
      if (res.id) setValue('providerTemplateId', res.id);
    },
    onError: (e: unknown) => pushToast('error', e instanceof ApiError ? e.message : (e as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: OutboundTemplatePatch) => {
      const current = templateQuery.data;
      if (!current) throw new Error('template_not_loaded');
      return updateTemplate(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.template.updated);
      queryClient.invalidateQueries({ queryKey: ['templates', id] });
      queryClient.invalidateQueries({ queryKey: ['template', id, templateId] });
      navigate(`/tenants/${id}/templates`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.template.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const onSubmit = handleSubmit((values) => {
    const parsed = templateFormSchema.safeParse(values);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const code = issue?.message ?? 'invalid';
      // Mapeo de códigos zod a strings amigables.
      const friendly: Record<string, string> = {
        TEMPLATE_NAME_INVALID: t.template.templateNameInvalid,
        CONNECTOR_REQUIRED: t.template.connectorRequired,
        LANGUAGE_INVALID: t.template.languageInvalid,
        PLACEHOLDERS_MAX: t.template.placeholdersMax,
      };
      pushToast('error', friendly[code] ?? code);
      return;
    }
    let components: Array<Record<string, unknown>> | undefined;
    try {
      components = parseComponents(values.componentsJson);
    } catch (e) {
      const msg = (e as Error).message;
      pushToast(
        'error',
        msg === 'COMPONENTS_INVALID_JSON'
          ? t.template.componentsInvalidJson
          : t.template.componentsMustBeArray,
      );
      return;
    }
    if (mode === 'create') {
      createMutation.mutate(valuesToCreate(values, components));
    } else if (templateQuery.data) {
      updateMutation.mutate(buildPatch(templateQuery.data, values, components));
    }
  });

  if (mode === 'edit' && templateQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && templateQuery.error) {
    return <p className="text-red-700">{(templateQuery.error as Error).message}</p>;
  }

  const submitting =
    isSubmitting || createMutation.isPending || updateMutation.isPending;
  const connectors = connectorsQuery.data ?? [];

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.template.newTitle : t.template.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.template.fields.name} error={errors.name?.message}>
          <input
            type="text"
            {...register('name', {
              required: t.template.nameRequired,
              maxLength: { value: 120, message: t.template.nameTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.template.fields.templateName}
          error={errors.templateName?.message}
        >
          <input
            type="text"
            placeholder="welcome_offer_v1"
            {...register('templateName', {
              required: t.template.templateNameRequired,
              pattern: {
                value: TEMPLATE_NAME_REGEX,
                message: t.template.templateNameInvalid,
              },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
        </Field>

        <Field
          label={t.template.fields.connector}
          error={errors.connectorId?.message}
        >
          <select
            {...register('connectorId', { required: t.template.connectorRequired })}
            disabled={mode === 'edit'}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
          >
            <option value="">—</option>
            {connectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.provider}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t.template.fields.language} error={errors.language?.message}>
          <input
            type="text"
            list="lang-presets"
            placeholder="es-CL"
            {...register('language', {
              required: t.template.languageInvalid,
              pattern: {
                value: LANGUAGE_REGEX,
                message: t.template.languageInvalid,
              },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
          <datalist id="lang-presets">
            {LANG_PRESETS.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
        </Field>

        <Field label={t.template.fields.category} error={errors.category?.message}>
          <select
            {...register('category')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t.template.fields.templateType}
          error={errors.templateType?.message}
        >
          <select
            {...register('templateType')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {TEMPLATE_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t.template.fields.status} error={errors.status?.message}>
          <select
            {...register('status')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.template.status[s]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t.template.fields.bodyText}
          error={errors.bodyText?.message}
          helper={`${bodyText?.length ?? 0}/1024`}
        >
          <textarea
            rows={5}
            maxLength={1024}
            {...register('bodyText', {
              required: t.template.bodyRequired,
              maxLength: { value: 1024, message: t.template.bodyTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">
            {t.template.fields.placeholders}
          </span>
          <Controller
            control={control}
            name="placeholders"
            render={({ field }) => (
              <TagInput
                value={field.value}
                onChange={field.onChange}
                placeholder={t.template.addPlaceholder}
                max={20}
                ariaLabel={t.template.fields.placeholders}
                testId="placeholders-input"
              />
            )}
          />
          <span className="mt-1 block text-xs text-slate-500">
            {t.template.placeholdersHelper}
          </span>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              {t.template.fields.components}
            </span>
            {/* #162 — toggle entre builder visual y JSON raw. */}
            <div className="flex gap-1 text-xs">
              <button
                type="button"
                onClick={() => setComponentsMode('visual')}
                className={`rounded px-2 py-0.5 ${
                  componentsMode === 'visual'
                    ? 'bg-brand-500 text-white'
                    : 'border border-slate-300 hover:bg-slate-50'
                }`}
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => setComponentsMode('json')}
                className={`rounded px-2 py-0.5 ${
                  componentsMode === 'json'
                    ? 'bg-brand-500 text-white'
                    : 'border border-slate-300 hover:bg-slate-50'
                }`}
              >
                JSON
              </button>
            </div>
          </div>
          {componentsMode === 'visual' ? (
            <TemplateComponentsBuilder
              value={builder}
              onChange={setBuilder}
              tenantId={id}
              connectorId={watch('connectorId')}
            />
          ) : !disableEditor ? (
            <Suspense
              fallback={<p className="text-xs text-slate-500">{t.app.loading}</p>}
            >
              <div className="h-48 overflow-hidden rounded border border-slate-200">
                <MonacoEditor
                  height="100%"
                  defaultLanguage="json"
                  value={componentsJson}
                  onChange={(v) => setValue('componentsJson', v ?? '')}
                  options={{ minimap: { enabled: false }, fontSize: 13 }}
                />
              </div>
            </Suspense>
          ) : (
            <textarea
              data-testid="components-fallback"
              rows={6}
              value={componentsJson}
              onChange={(e) => setValue('componentsJson', e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
            />
          )}
          <span className="mt-1 block text-xs text-slate-500">
            {t.template.componentsHelper}
          </span>
        </div>

        <details
          open={showAdvanced}
          onToggle={(e) =>
            setShowAdvanced((e.target as HTMLDetailsElement).open)
          }
        >
          <summary className="cursor-pointer text-sm font-medium text-slate-700">
            {t.template.fields.advanced}
          </summary>
          <div className="mt-3 space-y-3">
            <Field label={t.template.fields.providerTemplateId}>
              <input
                type="text"
                {...register('providerTemplateId')}
                className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </Field>
            <Field label={t.template.fields.externalId}>
              <input
                type="text"
                {...register('externalId')}
                className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </Field>
            <Field label={t.template.fields.namespace}>
              <input
                type="text"
                {...register('namespace')}
                className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </Field>
          </div>
        </details>

        <div className="flex justify-between items-center gap-2 pt-2">
          {/* #162 — Submit a provider Meta/360dialog (independiente del save local). */}
          <button
            type="button"
            onClick={() => submitToProviderMut.mutate()}
            disabled={submitToProviderMut.isPending || !watchedConnectorId}
            title="Envía esta definición al provider para aprobación (Meta/360dialog)"
            className="rounded border border-brand-300 bg-white px-3 py-1 text-sm text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {submitToProviderMut.isPending ? 'Enviando…' : 'Enviar al provider'}
          </button>
          <div className="flex gap-2">
            <Link
              to={`/tenants/${id}/templates`}
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
            >
              {t.app.cancel}
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting ? '…' : t.app.save}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  helper,
  children,
}: {
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && !error && (
        <span className="mt-1 block text-xs text-slate-500">{helper}</span>
      )}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}
